import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  Tooltip,
  alpha,
  InputBase,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SendIcon from '@mui/icons-material/Send';
import DeleteIcon from '@mui/icons-material/Delete';
import QRCode from 'react-qr-code';

interface CoinCardProps {
  coinName: string;
  coinSymbol: string;
  address: string;
  icon?: string;
  gradient?: string;
  qrValue?: string;
  handle?: string;
  userAddresses?: { [key: string]: string };
  onCopy?: () => void;
  onSend?: () => void;
  onDelete?: () => void;
}

export const CoinCard: React.FC<CoinCardProps> = ({
  coinName,
  coinSymbol,
  address,
  icon,
  gradient,
  qrValue,
  handle,
  userAddresses = {},
  onCopy,
  onSend,
  onDelete,
}) => {
  const [amount, setAmount] = useState('');

  return (
    <Card
      sx={{
        background: gradient || 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
        color: '#ffffff',
        borderRadius: 2.5,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        cursor: 'pointer',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 100%)',
          opacity: 0,
          transition: 'opacity 0.3s ease',
        },
        '&:hover': {
          transform: 'translateY(-6px) scale(1.02)',
          boxShadow: '0px 16px 48px rgba(99, 102, 241, 0.3)',
          '&::before': {
            opacity: 1,
          },
        },
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
          <Box flex={1}>
            <Box display="flex" alignItems="center" gap={2}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  background: 'rgba(255, 255, 255, 0.2)',
                  backdropFilter: 'blur(10px)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '2rem',
                  mb: 2,
                }}
              >
                {icon || coinSymbol.charAt(0)}
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={700}>
                  {coinName}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ opacity: 0.9, fontWeight: 600, fontSize: '0.875rem' }}
                >
                  {coinSymbol}
                </Typography>
              </Box>
            </Box>
          </Box>
          <Box
            sx={{
              p: 0.5,
              opacity: 1,
            }}
          >
            <QRCode
              value={
                amount && qrValue
                  ? `${qrValue}?amount=${amount}`
                  : qrValue || address
              }
              size={90}
              bgColor="transparent"
              fgColor="#ffffff"
            />
          </Box>
        </Box>

        <Box
          sx={{
            background: 'rgba(0, 0, 0, 0.15)',
            p: 1.5,
            px: 2,
            borderRadius: 1.5,
            fontFamily: 'monospace',
            fontSize: '0.875rem',
            wordBreak: 'break-all',
            mt: 2,
            mb: 0.5,
            backdropFilter: 'blur(10px)',
          }}
        >
          <Tooltip title={address} placement="top">
            <Typography variant="body2">{address}</Typography>
          </Tooltip>
        </Box>

        <InputBase
          placeholder="Amount (optional)"
          type="number"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          inputProps={{ step: '0.01', min: '0' }}
          sx={{
            width: '100%',
            mt: 2,
            mb: 2,
            p: 1,
            px: 2,
            borderRadius: 1.25,
            background: 'rgba(0,0,0,0.22)',
            color: '#fff',
            fontFamily: 'monospace',
            fontSize: '0.9rem',
            '& .MuiInputBase-input': {
              padding: 0,
              fontFamily: 'monospace',
            },
          }}
        />

        <Box display="flex" gap={1} justifyContent="flex-end">
          {onCopy && (
            <Tooltip title={amount ? "Copy Payment Request" : "Copy Link"}>
              <IconButton
                size="small"
                onClick={() => {
                  if (onCopy) {
                    // Pass amount to the onCopy callback via a custom URL parameter
                    if (amount) {
                      const link = `https://addrs.to/${handle}/${coinSymbol.toLowerCase()}?amount=${amount}`;
                      navigator.clipboard.writeText(link);
                    } else {
                      onCopy();
                    }
                  }
                }}
                sx={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  backdropFilter: 'blur(10px)',
                  color: '#ffffff',
                  '&:hover': {
                    background: 'rgba(255, 255, 255, 0.3)',
                    transform: 'scale(1.1)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {onSend && (
            <Tooltip title="Send Payment">
              <IconButton
                size="small"
                onClick={() => {
                  if (handle) {
                    if (amount) {
                      window.location.href = `/${handle}/${coinSymbol.toLowerCase()}?amount=${amount}`;
                    } else {
                      window.location.href = `/${handle}/${coinSymbol.toLowerCase()}`;
                    }
                  } else if (onSend) {
                    onSend();
                  }
                }}
                sx={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  backdropFilter: 'blur(10px)',
                  color: '#ffffff',
                  '&:hover': {
                    background: 'rgba(255, 255, 255, 0.3)',
                    transform: 'scale(1.1)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <SendIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {onDelete && (
            <Tooltip title="Remove Address">
              <IconButton
                size="small"
                onClick={onDelete}
                sx={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  backdropFilter: 'blur(10px)',
                  color: '#ffffff',
                  '&:hover': {
                    background: 'rgba(239, 68, 68, 0.4)',
                    transform: 'scale(1.1)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};
